import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import Autocomplete from "@mui/material/Autocomplete";
import CircularProgress from "@mui/material/CircularProgress";
import InputAdornment from "@mui/material/InputAdornment";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import Box from "@mui/material/Box";
import SearchIcon from "@mui/icons-material/Search";
import ArticleOutlinedIcon from "@mui/icons-material/ArticleOutlined";
import PersonOutlineOutlinedIcon from "@mui/icons-material/PersonOutlineOutlined";
import GavelOutlinedIcon from "@mui/icons-material/GavelOutlined";
import {
  ApplicationRole,
  LeadTableType,
  LeadsService,
  UsersService,
  UserShortResponse,
} from "../../api";

export type SearchPage = {
  title: string;
  to: string;
  keywords?: string;
  section?: string;
};

type SearchGroup = "Pages" | "Leads" | "Attorneys";

type SearchResult = {
  id: string;
  group: SearchGroup;
  title: string;
  subtitle?: string;
  to: string;
};

type TopbarSearchProps = {
  pages: SearchPage[];
  roles?: ApplicationRole[] | null;
};

const MIN_QUERY = 2;
const MAX_PER_GROUP = 6;

function pickLeadTableType(roles: ApplicationRole[]): LeadTableType | null {
  const set = new Set(roles);
  if (set.has(ApplicationRole.ADMIN)) return LeadTableType.ALL;
  if (set.has(ApplicationRole.REFFERAL_STAFF)) return LeadTableType.REFERRAL;
  if (set.has(ApplicationRole.STAFF) || set.has(ApplicationRole.QA)) {
    return LeadTableType.INTAKE;
  }
  if (set.has(ApplicationRole.STAFF_ATTORNEY)) return LeadTableType.REVIEW;
  if (set.has(ApplicationRole.ATTORNEY)) return LeadTableType.SHARED_ATTORNEY;
  return null;
}

function matchesPage(page: SearchPage, query: string): boolean {
  const haystack = `${page.title} ${page.keywords ?? ""} ${page.to}`.toLowerCase();
  return query.split(/\s+/).every((token) => haystack.includes(token));
}

function groupIcon(group: SearchGroup) {
  switch (group) {
    case "Leads":
      return <PersonOutlineOutlinedIcon fontSize="small" />;
    case "Attorneys":
      return <GavelOutlinedIcon fontSize="small" />;
    default:
      return <ArticleOutlinedIcon fontSize="small" />;
  }
}

function attorneyLabel(user: UserShortResponse): string {
  return (
    [user.firstName, user.lastName].filter(Boolean).join(" ").trim() ||
    user.email ||
    "Attorney"
  );
}

export default function TopbarSearch({ pages, roles = [] }: TopbarSearchProps) {
  const navigate = useNavigate();
  const inputRef = useRef<HTMLInputElement | null>(null);
  const attorneysCache = useRef<UserShortResponse[] | null>(null);
  const requestId = useRef(0);

  const [open, setOpen] = useState(false);
  const [inputValue, setInputValue] = useState("");
  const [options, setOptions] = useState<SearchResult[]>([]);
  const [loading, setLoading] = useState(false);

  const roleList = roles ?? [];
  const canSearchLeads = useMemo(
    () => pickLeadTableType(roleList) != null,
    [roleList]
  );
  const canSearchAttorneys = roleList.includes(ApplicationRole.ADMIN);

  const shortcutLabel = useMemo(() => {
    if (typeof navigator === "undefined") return "Ctrl K";
    return /Mac|iPhone|iPad|iPod/.test(navigator.platform) ? "⌘K" : "Ctrl K";
  }, []);

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === "k") {
        event.preventDefault();
        inputRef.current?.focus();
        setOpen(true);
      }
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, []);

  useEffect(() => {
    const query = inputValue.trim().toLowerCase();
    const currentRequest = ++requestId.current;

    if (!query) {
      setOptions(
        pages.slice(0, MAX_PER_GROUP).map((page) => ({
          id: `page-${page.to}`,
          group: "Pages" as const,
          title: page.title,
          subtitle: page.section,
          to: page.to,
        }))
      );
      setLoading(false);
      return;
    }

    const pageHits: SearchResult[] = pages
      .filter((page) => matchesPage(page, query))
      .slice(0, MAX_PER_GROUP)
      .map((page) => ({
        id: `page-${page.to}`,
        group: "Pages" as const,
        title: page.title,
        subtitle: page.section ?? page.to,
        to: page.to,
      }));

    const quickLinks: SearchResult[] = [];
    if (/^\d+$/.test(query) && canSearchLeads) {
      quickLinks.push({
        id: `lead-id-${query}`,
        group: "Leads",
        title: `Open lead #${query}`,
        subtitle: "Go directly by ID",
        to: `/lead/${query}`,
      });
    }
    if (/^\d+$/.test(query) && canSearchAttorneys) {
      quickLinks.push({
        id: `attorney-id-${query}`,
        group: "Attorneys",
        title: `Open attorney #${query}`,
        subtitle: "Go directly by ID",
        to: `/attorney/${query}`,
      });
    }

    const needsRemote =
      query.length >= MIN_QUERY && (canSearchLeads || canSearchAttorneys);

    if (!needsRemote) {
      setOptions([...pageHits, ...quickLinks]);
      setLoading(false);
      return;
    }

    setLoading(true);
    const timer = window.setTimeout(async () => {
      try {
        const remote: SearchResult[] = [];

        if (canSearchLeads) {
          const tableType = pickLeadTableType(roleList)!;
          const response = await LeadsService.postApiLeadsLeads({
            type: tableType,
            limit: MAX_PER_GROUP,
            offset: 0,
            sortBy: "UpdatedAt",
            sortAscending: false,
            filters: [
              { filterField: "LeadName", filterValue: inputValue.trim() },
            ],
          });
          for (const lead of response.leads ?? []) {
            remote.push({
              id: `lead-${lead.id}`,
              group: "Leads",
              title: lead.leadName?.trim() || `Lead #${lead.id}`,
              subtitle: [lead.category, lead.status, lead.employer]
                .filter(Boolean)
                .join(" · "),
              to: `/lead/${lead.id}`,
            });
          }
        }

        if (canSearchAttorneys) {
          if (!attorneysCache.current) {
            attorneysCache.current = await UsersService.postApiUsersGetUsersByRole(
              {
                role: ApplicationRole.ATTORNEY,
                includeInactive: true,
              }
            );
          }
          const q = query;
          const attorneyHits = (attorneysCache.current ?? [])
            .filter((user) => {
              const label = `${attorneyLabel(user)} ${user.email ?? ""} ${
                user.companyName ?? ""
              }`.toLowerCase();
              return q.split(/\s+/).every((token) => label.includes(token));
            })
            .slice(0, MAX_PER_GROUP);

          for (const user of attorneyHits) {
            if (!user.id) continue;
            remote.push({
              id: `attorney-${user.id}`,
              group: "Attorneys",
              title: attorneyLabel(user),
              subtitle: [user.companyName, user.email].filter(Boolean).join(" · "),
              to: `/attorney/${user.id}`,
            });
          }
        }

        if (currentRequest !== requestId.current) return;

        const seen = new Set<string>();
        const merged = [...pageHits, ...quickLinks, ...remote].filter((item) => {
          if (seen.has(item.id)) return false;
          seen.add(item.id);
          return true;
        });
        setOptions(merged);
      } catch {
        if (currentRequest !== requestId.current) return;
        setOptions([...pageHits, ...quickLinks]);
      } finally {
        if (currentRequest === requestId.current) {
          setLoading(false);
        }
      }
    }, 280);

    return () => window.clearTimeout(timer);
  }, [inputValue, pages, canSearchLeads, canSearchAttorneys, roleList]);

  const handleSelect = (result: SearchResult | null) => {
    if (!result) return;
    navigate(result.to);
    setInputValue("");
    setOpen(false);
    inputRef.current?.blur();
  };

  return (
    <Autocomplete
      className="app-topbar__search"
      open={open}
      onOpen={() => setOpen(true)}
      onClose={() => setOpen(false)}
      options={options}
      loading={loading}
      filterOptions={(x) => x}
      groupBy={(option) => option.group}
      getOptionLabel={(option) => option.title}
      isOptionEqualToValue={(a, b) => a.id === b.id}
      value={null}
      inputValue={inputValue}
      onInputChange={(_, value, reason) => {
        if (reason === "reset") return;
        setInputValue(value);
      }}
      onChange={(_, value) => handleSelect(value)}
      noOptionsText={
        inputValue.trim().length < MIN_QUERY
          ? "Type to search pages, leads, or attorneys"
          : "No matches"
      }
      renderOption={(props, option) => (
        <li {...props} key={option.id}>
          <Box
            sx={{
              display: "flex",
              flexDirection: "row",
              alignItems: "center",
              gap: 1.25,
              width: "100%",
              minWidth: 0,
            }}
          >
            <Box
              component="span"
              sx={{
                display: "inline-flex",
                flex: "0 0 auto",
                color: "text.secondary",
                lineHeight: 0,
              }}
            >
              {groupIcon(option.group)}
            </Box>
            <Typography
              variant="body2"
              noWrap
              fontWeight={600}
              sx={{ flex: "0 1 auto", minWidth: 0 }}
            >
              {option.title}
            </Typography>
            {option.subtitle && (
              <Typography
                variant="caption"
                color="text.secondary"
                noWrap
                sx={{ flex: "1 1 auto", minWidth: 0, textAlign: "right" }}
              >
                {option.subtitle}
              </Typography>
            )}
          </Box>
        </li>
      )}
      renderInput={(params) => (
        <TextField
          {...params}
          inputRef={inputRef}
          placeholder="Search pages, leads…"
          size="small"
          InputProps={{
            ...params.InputProps,
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" color="action" />
              </InputAdornment>
            ),
            endAdornment: (
              <>
                {loading ? (
                  <CircularProgress color="inherit" size={16} sx={{ mr: 1 }} />
                ) : (
                  <span className="app-topbar__search-shortcut">
                    {shortcutLabel}
                  </span>
                )}
                {params.InputProps.endAdornment}
              </>
            ),
          }}
        />
      )}
    />
  );
}
